import { useState } from "react";
import { useHistory } from "react-router-dom";
import be from "../../BE";
import auth from "../../Account/Auth";

function CompanyPersona({ setStage }) {
  const history = useHistory();

  const TEAM_SIZES = [
    { value: "1_10", label: "1-10" },
    { value: "11_20", label: "11-20" },
    { value: "21_50", label: "21-50" },
    { value: "50_plus", label: "50+" },
  ];

  const LOCATIONS = [
    "New York",
    "San Francisco",
    "London",
    "Berlin",
    "Amsterdam",
    "Other",
  ];

  const INDUSTRIES = [
    "Professional Services",
    "Consumer Services",
    "Marketing & Advertising",
    "Healthcare",
    "Education & Training",
    "Other",
  ];

  const [teamSize, setTeamSize] = useState("");
  const [location, setLocation] = useState("");
  const [industry, setIndustry] = useState("");
  const [saving, setSaving] = useState(false);

  const allSelected = teamSize && location && industry;

  const savePersona = async () => {
    if (!allSelected || saving) return;
    setSaving(true);

    const body = {
      industry,
      teamSize,
      location,
    };

    try {
      await auth
        .isAuthenticated()
        .then(() => {
          return be
            .post("Onboarding", "/onboarding/company/persona", body, true, true)
            .then((response) => {
              console.log("[onboarding][company-persona] persona saved");
            })
            .catch((error) => {
              console.error(
                "[onboarding][company-persona] network error while saving persona:",
                error
              );
            });
        })
        .catch((error) => {
          console.error(
            "[onboarding][company-persona] authentication error while saving persona:",
            error
          );
          history.push({ pathname: "/login" });
        });
    } catch (error) {
      console.error(
        "[onboarding][company-persona] functional error while saving persona:",
        error
      );
    } finally {
      setSaving(false);
      setStage && setStage("notifications");
    }
  };

  // "Maybe Later" -> send empty payload {}
  const skipPersona = async () => {
    if (saving) return;
    setSaving(true);

    try {
      await auth
        .isAuthenticated()
        .then(() => {
          return be
            .post("Onboarding", "/onboarding/company/persona", {}, true, true)
            .then((response) => {
              console.log(
                "[onboarding][company-persona] skipped (empty payload)"
              );
            })
            .catch((error) => {
              console.error(
                "[onboarding][company-persona] network error while skipping persona:",
                error
              );
            });
        })
        .catch((error) => {
          console.error(
            "[onboarding][company-persona] authentication error while skipping persona:",
            error
          );
          history.push({ pathname: "/login" });
        });
    } catch (error) {
      console.error(
        "[onboarding][company-persona] functional error while skipping persona:",
        error
      );
    } finally {
      setSaving(false);
      setStage && setStage("notifications");
    }
  };

  return (
    <div className="tw-max-w-3xl tw-mx-auto">
      <h1 className="tw-mb-3 tw-text-2xl tw-font-extrabold tw-tracking-tight tw-text-gray-900 tw-dark:text-white">
        What is your Target audience?
      </h1>
      <p className="tw-text-gray-600 tw-dark:text-gray-300 tw-mb-8">
        We’ll use these selections to tailor the leads and contacts we surface
        for you and to send helpful updates so you can quickly book appointments
        with the right people.
      </p>

      {/* Team size */}
      <section className="tw-mb-8">
        <h2 className="tw-text-sm tw-font-medium tw-text-gray-500 tw-uppercase tw-mb-3">
          Employee Count
        </h2>
        <ul
          className="tw-flex tw-flex-wrap tw-gap-2"
          role="radiogroup"
          aria-label="Employee Count"
        >
          {TEAM_SIZES.map((opt) => (
            <li key={opt.value}>
              <input
                type="radio"
                id={`size-${opt.value}`}
                name="teamSize"
                value={opt.value}
                checked={teamSize === opt.value}
                onChange={() => setTeamSize(opt.value)}
                className="!tw-hidden tw-peer"
                required
              />
              <label
                htmlFor={`size-${opt.value}`}
                className="tw-inline-flex tw-items-center tw-justify-center tw-px-4 tw-py-2 tw-text-gray-600 tw-border tw-border-gray-200 tw-rounded-full tw-cursor-pointer tw-bg-gray-50 hover:tw-bg-gray-100 peer-checked:tw-border-primary-600 peer-checked:tw-text-primary-600 tw-dark:text-gray-300 tw-dark:bg-gray-800 tw-dark:border-gray-700 tw-dark:hover:bg-gray-700"
              >
                {opt.label}
              </label>
            </li>
          ))}
        </ul>
      </section>

      {/* Locations */}
      <section className="tw-mb-8">
        <h2 className="tw-text-sm tw-font-medium tw-text-gray-500 tw-uppercase tw-mb-3">
          Locations
        </h2>
        <ul
          className="tw-flex tw-flex-wrap tw-gap-2"
          role="radiogroup"
          aria-label="Locations"
        >
          {LOCATIONS.map((city) => (
            <li key={city}>
              <input
                type="radio"
                id={`loc-${city}`}
                name="location"
                value={city}
                checked={location === city}
                onChange={() => setLocation(city)}
                className="!tw-hidden tw-peer"
                required
              />
              <label
                htmlFor={`loc-${city}`}
                className="tw-inline-flex tw-items-center tw-justify-center tw-px-4 tw-py-2 tw-text-gray-600 tw-border tw-border-gray-200 tw-rounded-full tw-cursor-pointer tw-bg-gray-50 hover:tw-bg-gray-100 peer-checked:tw-border-primary-600 peer-checked:tw-text-primary-600 tw-dark:text-gray-300 tw-dark:bg-gray-800 tw-dark:border-gray-700 tw-dark:hover:bg-gray-700"
              >
                {city}
              </label>
            </li>
          ))}
        </ul>
        <p className="tw-mt-2 tw-text-xs tw-text-gray-400">Choose one city.</p>
      </section>

      {/* Industry */}
      <section className="tw-mb-10">
        <h2 className="tw-text-sm tw-font-medium tw-text-gray-500 tw-uppercase tw-mb-3">
          Industry
        </h2>
        <ul
          className="tw-flex tw-flex-wrap tw-gap-2"
          role="radiogroup"
          aria-label="Industry"
        >
          {INDUSTRIES.map((ind) => (
            <li key={ind}>
              <input
                type="radio"
                id={`ind-${ind}`}
                name="industry"
                value={ind}
                checked={industry === ind}
                onChange={() => setIndustry(ind)}
                className="!tw-hidden tw-peer"
                required
              />
              <label
                htmlFor={`ind-${ind}`}
                className="tw-inline-flex tw-items-center tw-justify-center tw-px-4 tw-py-2 tw-text-gray-600 tw-border tw-border-gray-200 tw-rounded-full tw-cursor-pointer tw-bg-gray-50 hover:tw-bg-gray-100 peer-checked:tw-border-primary-600 peer-checked:tw-text-primary-600 tw-dark:text-gray-300 tw-dark:bg-gray-800 tw-dark:border-gray-700 tw-dark:hover:bg-gray-700"
              >
                {ind}
              </label>
            </li>
          ))}
        </ul>
      </section>

      <div className="tw-flex tw-items-center tw-gap-3">
        <button
          type="button"
          onClick={skipPersona}
          disabled={saving}
          className="tw-inline-flex tw-items-center tw-justify-center tw-rounded-xl tw-px-5 tw-py-3 tw-text-sm tw-font-semibold hover:tw-border hover:tw-border-gray-300 tw-text-gray-600 hover:tw-bg-gray-50 tw-transition"
        >
          Maybe Later
        </button>
        <button
          type="button"
          onClick={savePersona}
          disabled={!allSelected || saving}
          className={`tw-inline-flex tw-items-center tw-justify-center tw-rounded-xl tw-px-5 tw-py-3 tw-text-sm tw-font-semibold tw-transition
            ${
              allSelected && !saving
                ? "tw-bg-primary-600 tw-text-white hover:tw-bg-primary-700"
                : "tw-bg-gray-200 tw-text-gray-500 tw-cursor-not-allowed"
            }`}
        >
          {saving ? "Saving…" : "Save & Continue"}
        </button>

        {!allSelected && (
          <span className="tw-text-xs tw-text-gray-400">
            Select one option for each category.
          </span>
        )}
      </div>
    </div>
  );
}

export default CompanyPersona;
